import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import MemberTimeline from '../t2m/MemberTimeline';

// ─── LeadRecordPanel ──────────────────────────────────────────────────────────
// Slide-in panel triggered by "View Record" on any Lead Engine card.
// Shows prospect profile summary + full unified member timeline.
//
// Props:
//   actionId  — the action_id from prospect.id (passed via onSelectProspect)
//   onClose   — called when panel is dismissed
//
// Parent wiring (LeadEngine.jsx or wherever LeadContacts lives):
//   const [viewActionId, setViewActionId] = useState(null);
//   ...
//   <LeadContacts onSelectProspect={setViewActionId} ... />
//   {viewActionId && (
//     <LeadRecordPanel actionId={viewActionId} onClose={() => setViewActionId(null)} />
//   )}

export default function LeadRecordPanel({ actionId, memberId: directMemberId, memberName: directName, onClose }) {
  const { get } = useApi();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    // T2M / PB101 path — memberId passed directly, skip prospect lookup
    if (directMemberId && !actionId) {
      setProspect({ memberId: directMemberId, name: directName || null });
      setLoading(false);
      return;
    }
    if (!actionId) return;
    setLoading(true);
    get(`/lead/prospect/${actionId}`)
      .then(setProspect)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [actionId, directMemberId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-overlay"
        onClick={onClose}
        style={{ padding: 0 }}
      />
      {/* Panel — fixed position, sits above backdrop */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(480px, 100vw)',
          background: 'var(--surface-1, #111)',
          borderLeft: '1px solid var(--border, #222)',
          zIndex: 1001,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div>
            {loading ? (
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Loading…</div>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                  {prospect?.name || 'Member Record'}
                </div>
                {prospect?.email && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                    {prospect.email}
                  </div>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid var(--border)',
              color: 'var(--text-secondary)', borderRadius: 6,
              width: 32, height: 32, cursor: 'pointer',
              fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>

          {/* Prospect profile summary */}
          {!loading && prospect && (
            <div style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: '1.25rem',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px 16px',
            }}>
              <ProfileRow label="Pipeline"    value={prospect.pipeline?.replace(/_/g, ' ')} />
              <ProfileRow label="Score"       value={prospect.score != null ? prospect.score : null} />
              <ProfileRow label="Status"      value={prospect.status} highlight={prospect.status === 'active'} />
              <ProfileRow label="Tier"        value={prospect.tier} />
              {prospect.phone && <ProfileRow label="Phone" value={prospect.phone} span />}
              {prospect.membershipType && (
                <ProfileRow label="Membership" value={prospect.membershipType} span />
              )}
              {prospect.lastContact && (
                <ProfileRow label="Last Contact" value={new Date(prospect.lastContact).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} span />
              )}
              {prospect.followUpDate && (
                <ProfileRow
                  label="Follow-Up"
                  value={new Date(prospect.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  highlight
                  span
                />
              )}
            </div>
          )}

          {/* Timeline */}
          {!loading && prospect?.memberId && (
            <MemberTimeline
              memberId={prospect.memberId}
              memberName={prospect.name}
              compact={false}
            />
          )}

          {loading && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0' }}>
              Loading record…
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Small helper ─────────────────────────────────────────────────────────────
function ProfileRow({ label, value, highlight, span }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ gridColumn: span ? 'span 2' : undefined }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: highlight ? 'var(--yellow)' : 'var(--text)', fontWeight: highlight ? 600 : 400, textTransform: 'capitalize' }}>
        {String(value)}
      </div>
    </div>
  );
}
